import { Layout } from "@layouts";
import { Bar } from "@organisms";
import { PageBreadcrumbs } from "@molecules";
import { PagePaths } from "../../../../types";
import { Check } from "lucide-react";
import { LIFESTYLE_YEARLY_AMOUNT_LIMIT } from "@config/lifestyle";
import { useTranslation } from "react-i18next";

const colors = require("@theme/colors") as Record<string, any>;

export type LifestyleRulesPageTemplateProps = {
  navigateToPage: (to: string) => void;
  onReturnPressed: () => void;
  yearlyAmount: number;
  onYearlyAmountChange: (value: number) => void;
  coveragePercentage: number;
  onCoveragePercentageChange: (value: number) => void;
  allocationType: 'pro-rata' | 'full-amount';
  onAllocationTypeChange: (type: 'pro-rata' | 'full-amount') => void;
  onContinue: () => void;
};

export const LifestyleRulesPageTemplate = ({
  navigateToPage,
  onReturnPressed,
  yearlyAmount,
  onYearlyAmountChange,
  coveragePercentage,
  onCoveragePercentageChange,
  allocationType,
  onAllocationTypeChange,
  onContinue
}: LifestyleRulesPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const goToPerks = () => navigateToPage(`/dashboard/${PagePaths.perks}`);
  const goToLifestyleMain = () => navigateToPage(`/dashboard/${PagePaths.lifestyle}/main`);

  return (
    <Layout
      variant="dashboard"
      header={t("lifestyleRules.header")}
      subheader={
        <div className="flex flex-row justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("lifestyleRules.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("lifestyleRules.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("lifestyleRules.breadcrumbs.lifestyle"), onClick: goToLifestyleMain },
            { label: t("lifestyleRules.breadcrumbs.rules") },
          ]}
        />
        <div className="flex flex-col pb-8">
          {/* Numbered Title */}
          <div className="mb-8 mt-2">
            <h2 className="text-neutral-darkest text-xl font-normal mb-2">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  3.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("lifestyleRules.stepTitle")}
                </span>
              </div>
            </h2>
            <p className="text-gray-600">
              {t("lifestyleRules.stepDescription")}
            </p>
          </div>

          {/* General Rules Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("lifestyleRules.generalRules")}</h3>
            <div className="grid grid-cols-1 gap-6">
              {/* Yearly Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("lifestyleRules.yearlyAmountLabel")}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={yearlyAmount ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value === "") {
                        onYearlyAmountChange(LIFESTYLE_YEARLY_AMOUNT_LIMIT.min);
                        return;
                      }
                      const numericValue = Number(value);
                      const clampedValue = Math.min(
                        LIFESTYLE_YEARLY_AMOUNT_LIMIT.max,
                        Math.max(LIFESTYLE_YEARLY_AMOUNT_LIMIT.min, numericValue),
                      );
                      onYearlyAmountChange(clampedValue);
                    }}
                    className="w-28 pl-8 pr-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    min={LIFESTYLE_YEARLY_AMOUNT_LIMIT.min}
                    max={LIFESTYLE_YEARLY_AMOUNT_LIMIT.max}
                    placeholder="1000"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{t("lifestyleRules.yearlyAmountHelper")}</p>
              </div>

              {/* Coverage Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("lifestyleRules.coverageLabel")}
                </label>
                <div className="relative inline-block">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={coveragePercentage || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      const numValue = value ? Number(value) : 0;
                      if (numValue <= 100) {
                        onCoveragePercentageChange(numValue);
                      }
                    }}
                    className="w-28 pr-6 pl-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    placeholder="80"
                  />
                  <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t("lifestyleRules.coverageHelper")}</p>
              </div>
            </div>
          </div>

          {/* Allocation Choice Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("lifestyleRules.allocationTitle")}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t("lifestyleRules.allocationSubtitle")}
            </p>
            
            <div className="space-y-3" style={{ maxWidth: "680px" }}>
              {/* Pro-rata Option */}
              <div 
                className={`p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                  allocationType === 'pro-rata'
                    ? 'border-primary-mediumish bg-white hover:bg-primary-lightest'
                    : 'border-gray-200 bg-white hover:border-primary-mediumish hover:bg-primary-lightest'
                }`}
                onClick={() => onAllocationTypeChange('pro-rata')}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-xl text-gray-900">{t("lifestyleRules.allocationOptions.proRataTitle")}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("lifestyleRules.allocationOptions.proRataDescription")}
                    </p>
                  </div>
                  <div
                    className={`h-7 w-7 rounded-full border-2 flex items-center justify-center ${
                      allocationType === 'pro-rata'
                        ? "border-primary-medium bg-primary-medium text-white"
                        : "border-neutral-light bg-white text-transparent"
                    }`}
                  >
                    <Check className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Full Amount Option */}
              <div 
                className={`p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                  allocationType === 'full-amount'
                    ? 'border-primary-mediumish bg-white hover:bg-primary-lightest'
                    : 'border-gray-200 bg-white hover:border-primary-mediumish hover:bg-primary-lightest'
                }`}
                onClick={() => onAllocationTypeChange('full-amount')}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-xl text-gray-900">{t("lifestyleRules.allocationOptions.fullAmountTitle")}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("lifestyleRules.allocationOptions.fullAmountDescription")}
                    </p>
                  </div>
                  <div
                    className={`h-7 w-7 rounded-full border-2 flex items-center justify-center ${
                      allocationType === 'full-amount'
                        ? "border-primary-medium bg-primary-medium text-white"
                        : "border-neutral-light bg-white text-transparent"
                    }`}
                  >
                    <Check className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={onContinue}
          isContinueDisabled={yearlyAmount <= 0 || coveragePercentage <= 0}
        />
      </div>
    </Layout>
  );
}; 
